/**
 * Date Utilities for ZRM
 */

import {
  format,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  getQuarter,
  getYear,
  addMonths,
  subMonths,
  parseISO,
  isValid,
  differenceInCalendarDays,
  isFuture,
  isToday,
} from 'date-fns'

// Format date for display
export const formatDate = (date: string | Date, formatStr: string = 'MMM d, yyyy'): string => {
  const parsed = typeof date === 'string' ? parseISO(date) : date
  return isValid(parsed) ? format(parsed, formatStr) : ''
}

// Format date for input field (YYYY-MM-DD)
export const formatDateForInput = (date: Date): string => {
  return format(date, 'yyyy-MM-dd')
}

// Get today's date formatted for input
export const getTodayForInput = (): string => {
  return formatDateForInput(new Date())
}

// Check if date is in the future
export const isFutureDate = (date: string): boolean => {
  const parsed = parseISO(date)
  return isValid(parsed) && isFuture(parsed) && !isToday(parsed)
}

// Get current quarter
export const getCurrentQuarter = (): number => {
  return getQuarter(new Date())
}

// Get current year
export const getCurrentYear = (): number => {
  return getYear(new Date())
}

// Get quarter date range
export const getQuarterDateRange = (quarter: 1 | 2 | 3 | 4, year: number): { start: string; end: string } => {
  // Create a date in the middle of the target quarter
  const monthOffset = (quarter - 1) * 3
  const quarterDate = new Date(year, monthOffset, 15)

  const start = startOfQuarter(quarterDate)
  const end = endOfQuarter(quarterDate)

  return {
    start: formatDateForInput(start),
    end: formatDateForInput(end),
  }
}

// Get month date range
export const getMonthDateRange = (month: number, year: number): { start: string; end: string } => {
  const monthDate = new Date(year, month - 1, 15)

  return {
    start: formatDateForInput(startOfMonth(monthDate)),
    end: formatDateForInput(endOfMonth(monthDate)),
  }
}

// Get current month date range
export const getCurrentMonthDateRange = (): { start: string; end: string } => {
  const now = new Date()
  return {
    start: formatDateForInput(startOfMonth(now)),
    end: formatDateForInput(endOfMonth(now)),
  }
}

// Get month names for a quarter
export const getQuarterMonthNames = (quarter: 1 | 2 | 3 | 4, year: number): string[] => {
  const monthOffset = (quarter - 1) * 3
  const months: string[] = []

  for (let i = 0; i < 3; i++) {
    const monthDate = new Date(year, monthOffset + i, 1)
    months.push(format(monthDate, 'MMMM'))
  }

  return months
}

// Get short month names for a quarter
export const getQuarterMonthShortNames = (quarter: 1 | 2 | 3 | 4, year: number): string[] => {
  const monthOffset = (quarter - 1) * 3
  const months: string[] = []

  for (let i = 0; i < 3; i++) {
    const monthDate = new Date(year, monthOffset + i, 1)
    months.push(format(monthDate, 'MMM'))
  }

  return months
}

// Get available years for selection (current year and 2 years back)
export const getAvailableYears = (): number[] => {
  const currentYear = getCurrentYear()
  return [currentYear, currentYear - 1, currentYear - 2]
}

// Get available quarters
export const getAvailableQuarters = (): Array<{ value: 1 | 2 | 3 | 4; label: string }> => {
  return [
    { value: 1, label: 'Q1 (Jan - Mar)' },
    { value: 2, label: 'Q2 (Apr - Jun)' },
    { value: 3, label: 'Q3 (Jul - Sep)' },
    { value: 4, label: 'Q4 (Oct - Dec)' },
  ]
}

// "3 weeks" / "1 week" — a count that agrees with its unit.
const countOf = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? '' : 's'}`

/**
 * Relative label for an activity date, in either direction.
 *
 * Activities can be scheduled ahead since #1274, so a date in the future is
 * ordinary data rather than an impossibility. The previous implementation
 * subtracted in one direction only and let a negative difference fall through
 * the "days ago" branch, rendering "-21 days ago" for something three weeks
 * away (#1306).
 *
 * Calendar days, not elapsed milliseconds: "yesterday" means the previous
 * date, whatever the time of day either side of midnight.
 */
export const getRelativeDateLabel = (date: string): string => {
  const parsed = parseISO(date)
  if (!isValid(parsed)) return ''

  if (isToday(parsed)) return 'Today'

  // Positive = behind us, negative = ahead of us.
  const diffDays = differenceInCalendarDays(new Date(), parsed)

  if (diffDays > 0) {
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${countOf(diffDays, 'day')} ago`
    if (diffDays < 30) return `${countOf(Math.floor(diffDays / 7), 'week')} ago`
    return formatDate(date, 'MMM d')
  }

  const daysAhead = -diffDays
  if (daysAhead === 1) return 'Tomorrow'
  if (daysAhead < 7) return `in ${countOf(daysAhead, 'day')}`
  if (daysAhead < 30) return `in ${countOf(Math.floor(daysAhead / 7), 'week')}`
  return formatDate(date, 'MMM d')
}

// Previous month helper
export const getPreviousMonth = (date: Date): Date => {
  return subMonths(date, 1)
}

// Next month helper
export const getNextMonth = (date: Date): Date => {
  return addMonths(date, 1)
}

// Get the Monday of the current week
export const getCurrentWeekStart = (): string => {
  return formatDateForInput(startOfWeek(new Date(), { weekStartsOn: 1 }))
}

// Get week date range (Monday to Sunday)
export const getWeekDateRange = (weekStart: string): { start: string; end: string } => {
  const start = parseISO(weekStart)
  return {
    start: weekStart,
    end: formatDateForInput(endOfWeek(start, { weekStartsOn: 1 })),
  }
}

// Get day name labels for a week (e.g., "Mon 16", "Tue 17", ...)
export const getWeekDayNames = (weekStart: string): string[] => {
  const start = parseISO(weekStart)
  return Array.from({ length: 7 }, (_, i) => {
    const day = addDays(start, i)
    return format(day, 'EEE d')
  })
}

// Get week boundaries (BucketBoundary-shaped) for weeks within a month
export const getMonthWeekBoundaries = (month: number, year: number): Array<{ start: string; end: string; label: string }> => {
  const monthDate = new Date(year, month - 1, 1)
  const monthStart = startOfMonth(monthDate)
  const monthEnd = endOfMonth(monthDate)
  const boundaries: Array<{ start: string; end: string; label: string }> = []

  let weekNum = 1
  let current = monthStart
  while (current <= monthEnd) {
    const weekEnd = endOfWeek(current, { weekStartsOn: 1 })
    const boundaryEnd = weekEnd > monthEnd ? monthEnd : weekEnd
    boundaries.push({
      start: formatDateForInput(current),
      end: formatDateForInput(boundaryEnd),
      label: `Wk ${weekNum}`,
    })
    weekNum++
    current = addDays(boundaryEnd, 1)
  }

  return boundaries
}

// Format week label for display (e.g., "Feb 16 – Feb 22, 2026")
export const formatWeekLabel = (weekStart: string): string => {
  const start = parseISO(weekStart)
  const end = endOfWeek(start, { weekStartsOn: 1 })
  return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`
}

// Format month label for display (e.g., "February 2026")
export const formatMonthLabel = (month: number, year: number): string => {
  const monthDate = new Date(year, month - 1, 1)
  return format(monthDate, 'MMMM yyyy')
}

// Navigate weeks (returns prev/next week start dates)
export const getWeekNavigation = (weekStart: string): { prev: string; next: string } => {
  const start = parseISO(weekStart)
  return {
    prev: formatDateForInput(subWeeks(start, 1)),
    next: formatDateForInput(addWeeks(start, 1)),
  }
}

// Navigate months (returns prev or next month/year)
export const getMonthNavigation = (month: number, year: number, _direction?: 'prev' | 'next'): {
  prevMonth: number; prevYear: number; nextMonth: number; nextYear: number
} => {
  const monthDate = new Date(year, month - 1, 1)
  const prev = subMonths(monthDate, 1)
  const next = addMonths(monthDate, 1)
  return {
    prevMonth: prev.getMonth() + 1,
    prevYear: prev.getFullYear(),
    nextMonth: next.getMonth() + 1,
    nextYear: next.getFullYear(),
  }
}
